import "regenerator-runtime";
import * as mutations from "../graphql/mutations";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import OverallProgress from "../components/OverallProgress";
import ImageModal from "../components/modals/ImageModal";
import LoadingModal from "../components/modals/LoadingModal";
import NotificationSnackbar from "../components/NotificationSnackbar";
import AnswerQuestion from "./AnswerQuestion";
import React, { useState, useEffect } from "react";
import { apiClient } from "../helpers/apiClient";
import {
  sortDataBySortingNumber,
  sortRequirementsByWeight,
} from "../helpers/helpers";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Button, Divider, Grid, Typography } from "@mui/material";
import {
  CGI_BLACK,
  CGI_GRADIENT_B,
  CGI_SUCCESS,
  COMPASS_GRADIENT,
  packages,
  versions,
} from "../helpers/enums";
import {
  fetchAssessmentAssignmentById,
  fetchAssessmentById,
  fetchCustomerById,
  fetchModulesByAssessment,
  fetchPerformedAssessmentByAssessmentAssignment,
  fetchQuestionsByModule,
  fetchQuestionsByModuleFilteredByPackage,
  fetchRecommendationsByRequirement,
  fetchRequirementAnswerByRequirement,
  fetchRequirementsByQuestion,
  fetchStructuredEntriesByModule,
} from "../helpers/apiRequests";

function createEmptyStructuredAnswer(detail = {}) {
  return {
    xAxis: {
      extent: 0,
      scope: 0,
      immutability: 0,
      probability: 0,
    },
    yAxis: {
      extent: 0,
      probability: 0,
    },
    relevance: 0,
    disabled: false,
    commentImpact: "",
    commentFinancial: "",
    commentStakeholder: "",
    risk: detail.risk || "",
    opportunity: detail.opportunity || "",
    example: detail.example || "",
  };
}

function parseStructuredAnswerPayload(answer) {
  if (!answer?.comment) {
    return { answerText: "", responsibleRole: "", entries: [] };
  }

  try {
    const parsed = JSON.parse(answer.comment);
    return {
      answerText: parsed.answerText || "",
      responsibleRole: parsed.responsibleRole || answer.contactPerson || "",
      entries: Array.isArray(parsed.entries) ? parsed.entries : [],
    };
  } catch {
    return {
      answerText: answer.comment || "",
      responsibleRole: answer.contactPerson || "",
      entries: [],
    };
  }
}

export default function PerformAssessment() {
  const { t } = useTranslation();
  const { assignedAssessment } = useParams();
  const navigate = useNavigate();

  const emptyAssessment = {
    description: "",
    name: "",
    type: "",
    modules: [],
    imageKey: "",
  };
  const emptyModule = { module: { id: "" }, questionWithAnswer: [] };
  const info = { comment: "", contactPerson: "" };
  const emptyCustomer = { name: "" };

  const [openNotification, setOpenNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState("");
  const [openLoading, setOpenLoading] = useState(true);
  const [selectedModule, setSelectedModule] = useState(emptyModule);
  const [selectedAssessment, setSelectedAssessment] = useState(emptyAssessment);
  const [assessmentVersion, setAssessmentVersion] = useState(0);
  const [assessmentPackage, setAssessmentPackage] = useState("");
  const [customer, setCustomer] = useState(emptyCustomer);
  const [moduleData, setModuleData] = useState([]);
  const [performedAssessmentId, setPerformedAssessmentId] = useState("");
  const [overAllProgress, setOverAllProgress] = useState(0);
  const [isNotReadyToFinish, setIsNotReadyToFinish] = useState(true);

  function isQuestionAnswered(questionWithAnswer) {
    if (questionWithAnswer.question.type === "STRUCTURED_ENTRY") {
      return !!questionWithAnswer.answerInfo?.id;
    }

    return (
      questionWithAnswer.requirementsWithAnswers.filter(
        (req) => req.answer?.id,
      ).length > 0
    );
  }

  async function fetchModuleData(
    module,
    assignedPackage,
    isStructured,
    currentPerformedAssessmentId,
  ) {
    try {
      let questionWithAnswer = [];

      if (!isStructured) {
        // Klassische Questions + Requirements
        let questions =
          packages[assignedPackage] === packages.BASIC
            ? await fetchQuestionsByModuleFilteredByPackage(
                module.id,
                packages[assignedPackage],
              )
            : await fetchQuestionsByModule(module.id);

        questionWithAnswer = await Promise.all(
          questions.map(async (question) => {
            const requirements = await fetchRequirementsByQuestion(question.id);

            const requirementsWithAnswers = await Promise.all(
              requirements.map(async (req) => {
                const fetchedRecs = await fetchRecommendationsByRequirement(
                  req.id,
                );
                const requirementWithRecs = {
                  ...req,
                  recommendations: fetchedRecs.items || [],
                };

                const fetchedAnswer =
                  await fetchRequirementAnswerByRequirement(req.id);
                const answer = (fetchedAnswer.items || []).find(
                  (item) =>
                    item.performedAssessmentID === currentPerformedAssessmentId,
                ) ?? {
                  requirementID: req.id,
                  fulfilled: false,
                  selectedLevel: 0,
                  irrelevant: false,
                };

                return { requirement: requirementWithRecs, answer };
              }),
            );
            requirementsWithAnswers.sort((a, b) =>
              sortRequirementsByWeight(a.requirement, b.requirement),
            );

            const firstRealAnswer = requirementsWithAnswers.find(
              ({ answer }) => answer?.comment || answer?.contactPerson,
            );
            const answerInfo = firstRealAnswer
              ? {
                  comment: firstRealAnswer.answer.comment || "",
                  contactPerson: firstRealAnswer.answer.contactPerson || "",
                }
              : info;

            return { question, answerInfo, requirementsWithAnswers };
          }),
        );
      } else {
        // StructuredEntries + EntryDetails
        const structuredEntries = await fetchStructuredEntriesByModule(
          module.id,
        );
        questionWithAnswer = await Promise.all(
          structuredEntries.map(async (entry) => {
            const fetchedAnswer =
              await fetchRequirementAnswerByRequirement(entry.id);
            const answer = (fetchedAnswer.items || []).find(
              (item) =>
                item.performedAssessmentID === currentPerformedAssessmentId,
            );
            const parsedAnswer = parseStructuredAnswerPayload(answer);

            return {
              question: {
                id: entry.id,
                title: entry.title,
                description: entry.description,
                source: entry.source,
                threshold: entry.threshold,
                type: "STRUCTURED_ENTRY",
              },
              answerInfo: {
                id: answer?.id || "",
                comment: parsedAnswer.answerText,
                contactPerson: parsedAnswer.responsibleRole,
              },
              requirementsWithAnswers: (entry.entries || []).map(
                (detail, index) => ({
                  requirement: {
                    id: `${entry.id}:${index}`,
                    text: detail.impact,
                    impact: detail.impact,
                    risk: detail.risk,
                    opportunity: detail.opportunity,
                    example: detail.example,
                    entryIndex: index,
                  },
                  answer: {
                    ...createEmptyStructuredAnswer(detail),
                    ...(parsedAnswer.entries?.[index] || {}),
                  },
                }),
              ),
            };
          }),
        );
      }

      return { module, questionWithAnswer };
    } catch (error) {
      console.error(error);
      return { module, questionWithAnswer: [] };
    }
  }

  async function fetchAssessmentData() {
    setOpenLoading(true);
    try {
      const assessmentAssignment =
        await fetchAssessmentAssignmentById(assignedAssessment);
      setAssessmentVersion(assessmentAssignment.version);
      setAssessmentPackage(assessmentAssignment.package);
      const assignedPackage = assessmentAssignment.package;

      const c = await fetchCustomerById(assessmentAssignment.customerID);
      setCustomer(c);

      const performed =
        await fetchPerformedAssessmentByAssessmentAssignment(
          assignedAssessment,
        );
      const currentPerformedAssessmentId =
        performed.items.length > 0 ? performed.items[0].id : "";
      setPerformedAssessmentId(currentPerformedAssessmentId);

      const fetchedAssessment = await fetchAssessmentById(
        assessmentAssignment.assessmentID,
      );
      const assessment = {
        name: fetchedAssessment.name,
        contactPerson: fetchedAssessment.contactPerson,
        language: fetchedAssessment.language,
        type: fetchedAssessment.type,
        description: fetchedAssessment.description,
        imageKey: fetchedAssessment.imageKey || "",
      };

      const isStructured =
        versions[fetchedAssessment.type] === versions.TWO_DIMENSIONAL;

      const fetchedModules = await fetchModulesByAssessment(
        fetchedAssessment.id,
      );
      const sortedModules = Array.from(
        new Map((fetchedModules || []).map((module) => [module.id, module]))
          .values(),
      ).sort(sortDataBySortingNumber);

      const fetchedModuleData = await Promise.all(
        sortedModules.map((m) =>
          fetchModuleData(
            m,
            assignedPackage,
            isStructured,
            currentPerformedAssessmentId,
          ),
        ),
      );
      const modules = sortedModules.map((m) => ({
        id: m.id,
        sortingNumber: m.sortingNumber,
        title: m.title,
      }));

      assessment.modules = modules;
      setModuleData(fetchedModuleData);
      setSelectedAssessment(assessment);
    } catch (error) {
      console.error(error);
      navigate("/assessmentsEvaluations");
    }
    setOpenLoading(false);
  }

  async function completeAssessment() {
    try {
      await apiClient.graphql({
        query: mutations.updatePerformAssessment,
        variables: {
          input: { id: performedAssessmentId, assessmentState: "COMPLETED" },
        },
      });
      setNotificationMessage("assessmentCompleted");
    } catch (error) {
      console.error(error);
      setNotificationMessage("errorMessage");
    }
    navigate("/assessmentsEvaluations");
    setOpenNotification(true);
  }

  async function computeOverallProgress() {
    let totalQuestions = 0;
    let answeredQuestions = 0;

    moduleData.forEach((module) => {
      const questionsCount = module.questionWithAnswer.length;
      const answeredCount = module.questionWithAnswer.filter((q) => {
        return isQuestionAnswered(q);
      }).length;

      totalQuestions += questionsCount;
      answeredQuestions += answeredCount;
    });

    if (totalQuestions === 0) return 0;

    const progress = (answeredQuestions / totalQuestions) * 100;
    setOverAllProgress(progress);
    setIsNotReadyToFinish(progress !== 100);
  }

  useEffect(() => {
    fetchAssessmentData();
  }, [assignedAssessment]);
  useEffect(() => {
    computeOverallProgress();
  }, [selectedAssessment, moduleData]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", mx: 2 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: 1,
          borderColor: CGI_BLACK,
        }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() =>
            selectedModule.module.id === ""
              ? navigate("/assessmentsEvaluations")
              : setSelectedModule({ module: { id: "" } })
          }
        >
          {selectedModule.module.id === ""
            ? t("backToCustomerSelection")
            : t("backToModuleSelection")}
        </Button>
        <Box
          sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}
        >
          <Typography sx={{ ml: 3, fontSize: 16, fontWeight: 500 }}>{`${t(
            "customer",
          )}: ${customer.name}`}</Typography>
          <Typography sx={{ fontSize: 16, ml: 2, pl: 2, borderLeft: 1 }}>{`${t(
            "assessment",
          )}: "${selectedAssessment.name} (${assessmentPackage})"`}</Typography>
          <Typography sx={{ fontSize: 16, ml: 2, pl: 2, borderLeft: 1 }}>{`${t(
            "version",
          )}: ${assessmentVersion}`}</Typography>
        </Box>
      </Box>

      {selectedModule.module.id === "" ? (
        <>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "flex-end",
            }}
          >
            <Button
              variant="contained"
              disabled={isNotReadyToFinish}
              endIcon={<DoneAllIcon />}
              onClick={() => completeAssessment()}
            >
              {t("completeAssessment")}
            </Button>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "row", mt: 3.5, ml: 2 }}>
            <ImageModal
              imageKey={selectedAssessment?.imageKey}
              boxWidth={230}
              boxHeight={230}
            />
            <Divider orientation="vertical" flexItem sx={{ mx: 3 }} />
            <Typography
              align="justify"
              sx={{
                mr: { xs: 2.5, lg: 20 },
                fontSize: 15,
                fontFamily: "Inter",
              }}
            >
              {selectedAssessment?.description}
            </Typography>
          </Box>
          <Divider sx={{ my: 3 }} />

          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography
              sx={{
                mb: 3,
                pr: 2,
                fontSize: 22,
                borderBottom: 1,
                userSelect: "none",
                mx: 2,
              }}
            >
              {versions[selectedAssessment.type] === versions.TWO_DIMENSIONAL
                ? `${t("selectTopic")}:`
                : `${t("selectModule")}:`}
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                width: 370,
                mr: 3,
                pl: 3,
                alignItems: "center",
                pb: 1,
                mb: 2,
                borderBottom: 1,
                borderColor: CGI_BLACK,
              }}
            >
              <Typography sx={{ width: 125, userSelect: "none" }}>
                {t("progress:")}
              </Typography>
              <OverallProgress value={overAllProgress} />
            </Box>
          </Box>

          <Grid
            container
            spacing={1}
            rowSpacing={5}
            justifyContent="center"
            alignItems="center"
          >
            {moduleData.length === 0 && (
              <Typography sx={{ fontSize: 18, mt: 15 }}>
                {t("noModule")}
              </Typography>
            )}
            {moduleData.map((mData, index) => (
              <React.Fragment key={index}>
                {mData.questionWithAnswer.length !== 0 && (
                  <Grid
                    item
                    xs={6}
                    lg={4}
                    sx={{
                      flexDirection: "column",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        width: 7 / 10,
                        borderRadius: 20,
                        border: "double 1px transparent",
                        backgroundImage:
                          mData.questionWithAnswer.filter(isQuestionAnswered)
                            .length === mData.questionWithAnswer.length
                            ? "linear-gradient(white, white)," +
                              COMPASS_GRADIENT
                            : "linear-gradient(white, white)," + CGI_GRADIENT_B,
                        backgroundOrigin: "border-box",
                        backgroundClip: "content-box, border-box",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          alignItems: "center",
                          width: 0.85,
                          borderRadius: 20,
                          border: "double 3px transparent",
                          backgroundImage:
                            mData.questionWithAnswer.filter(isQuestionAnswered)
                              .length === mData.questionWithAnswer.length
                              ? "linear-gradient(white, white)," +
                                COMPASS_GRADIENT
                              : "linear-gradient(white, white)," +
                                CGI_GRADIENT_B,
                          backgroundOrigin: "border-box",
                          backgroundClip: "content-box, border-box",
                          m: 2.5,
                          "&:hover": {
                            cursor: "pointer",
                            transform: "scale(1.05)",
                            boxShadow: 4,
                          },
                        }}
                      >
                        <Box
                          onClick={() => setSelectedModule(mData)}
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center",
                            width: 1,
                            py: 1.5,
                          }}
                        >
                          <Typography
                            sx={{
                              textAlign: "center",
                              fontSize: 16,
                              fontWeight: 500,
                              userSelect: "none",
                              px: 3,
                            }}
                          >
                            {mData.module.title}
                          </Typography>
                          <Divider
                            flexItem
                            sx={{
                              my: 1,
                              mx: 2,
                              borderImageSlice: 1,
                              backgroundImage:
                                mData.questionWithAnswer.filter(
                                  isQuestionAnswered,
                                ).length === mData.questionWithAnswer.length
                                  ? COMPASS_GRADIENT
                                  : CGI_GRADIENT_B,
                            }}
                          />
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "row",
                              width: 1,
                              justifyContent: "center",
                            }}
                          >
                            <Typography
                              align="center"
                              sx={{
                                mx: 2,
                                mt: 1,
                                userSelect: "none",
                                color:
                                  mData.questionWithAnswer.filter(
                                    isQuestionAnswered,
                                  ).length === mData.questionWithAnswer.length
                                    ? CGI_SUCCESS
                                    : CGI_BLACK,
                              }}
                            >
                              {
                                mData.questionWithAnswer.filter(
                                  isQuestionAnswered,
                                ).length
                              }
                              /{mData.questionWithAnswer.length}{" "}
                              {t("questionAnswered")}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </Grid>
                )}
              </React.Fragment>
            ))}
          </Grid>

          <LoadingModal open={openLoading} />
        </>
      ) : (
        <AnswerQuestion
          performedAssessmentId={performedAssessmentId}
          assessmentAssignmentID={assignedAssessment}
          setOpenNotification={setOpenNotification}
          setNotificationMessage={setNotificationMessage}
          selectedModule={selectedModule}
          setSelectedModule={setSelectedModule}
        />
      )}

      <NotificationSnackbar
        message={notificationMessage}
        open={openNotification}
        setOpen={setOpenNotification}
      />
    </Box>
  );
}
